import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail, LOGO_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Smartphone } from "lucide-react";

export default function AdminAuth() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState("phone"); // phone | code
  const [numbers, setNumbers] = useState([]);
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/approved-numbers").then((r) => setNumbers(r.data)).catch(() => setNumbers([]));
  }, []);

  const sendCode = async (num) => {
    setError(""); setBusy(true); setPhone(num);
    try {
      await api.post("/admin/request-code", { phone_number: num });
      setStep("code");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const checkCode = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      const { data } = await api.post("/admin/verify-code", { phone_number: phone, code });
      setUser(data);
      navigate("/admin/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#2D2336] px-6">
      <div className="w-full max-w-md bg-white rounded-2xl p-10 shadow-2xl">
        <img src={LOGO_URL} alt="UDC" className="h-14 mx-auto" />
        <h1 className="font-serif text-3xl text-[#2D2336] text-center mt-5">Admin Portal</h1>

        {step === "phone" && (
          <div className="mt-8 space-y-5" data-testid="phone-step">
            <p className="text-center text-[#5C5166] text-sm flex items-center justify-center gap-2">
              <Smartphone className="h-4 w-4" /> Sign in with a code sent to an approved number
            </p>
            {numbers.length === 0 && <p className="text-center text-sm text-[#964545]">No approved numbers configured.</p>}
            {numbers.map((n) => (
              <button key={n.value} disabled={busy} onClick={() => sendCode(n.value)} data-testid={`send-code-${n.value}`}
                className="w-full border border-[#6B4C8A] text-[#6B4C8A] py-3 rounded-full font-medium hover:bg-[#F3EFF7] transition-colors disabled:opacity-60">
                Text code to {n.label}
              </button>
            ))}
            {error && <p className="text-sm text-[#964545] text-center" data-testid="phone-error">{error}</p>}
          </div>
        )}

        {step === "code" && (
          <form onSubmit={checkCode} className="mt-8 space-y-5" data-testid="code-step">
            <p className="text-center text-[#5C5166] text-sm">
              Enter the 6-digit code sent to <strong>{numbers.find((n) => n.value === phone)?.label}</strong>
            </p>
            <div>
              <Label className="text-[#2D2336]">Access Code</Label>
              <Input inputMode="numeric" required autoFocus data-testid="code-input" value={code}
                onChange={(e) => setCode(e.target.value)} className="mt-2 tracking-widest text-center text-lg" placeholder="••••••" />
            </div>
            {error && <p className="text-sm text-[#964545]" data-testid="code-error">{error}</p>}
            <button type="submit" disabled={busy} data-testid="code-submit"
              className="w-full bg-[#6B4C8A] text-white py-3 rounded-full font-medium hover:bg-[#543C6E] transition-colors disabled:opacity-60">
              {busy ? "Verifying…" : "Sign In"}
            </button>
            <button type="button" onClick={() => { setStep("phone"); setCode(""); setError(""); }}
              className="w-full text-sm text-[#78698A] hover:text-[#6B4C8A]">← Use a different number</button>
          </form>
        )}

        <a href="/" className="block text-center text-sm text-[#78698A] mt-6 hover:text-[#6B4C8A]">← Back to site</a>
      </div>
    </div>
  );
}
